import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Download, FileText, Settings2, TrendingUp, ShieldCheck, CheckCircle2, AlertTriangle, Camera, Clock } from "lucide-react";
import html2canvas from "html2canvas";
import api from "@/lib/api";
import { fmtDate } from "@/lib/date";
import { BUCKET_COLORS } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";

const inr = (n) => (n ? Number(Math.round(n)).toLocaleString("en-IN") : "—");
const num = (n) => (n ? Number(Math.round(n)).toLocaleString("en-IN") : "—");

function downloadCsv(name, header, rows) {
  const lines = [header.join(",")].concat(
    rows.map((r) => r.map((c) => (typeof c === "string" && c.includes(",") ? `"${c}"` : c)).join(","))
  );
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  toast.success("CSV exported");
}

function Loading() {
  return <div className="p-6 space-y-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>;
}

function useNow() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

const stamp = (d) => d.toLocaleString("en-IN", {
  day: "2-digit", month: "2-digit", year: "numeric",
  hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
});

async function copyCardImage(el, setBusy) {
  if (!el) return;
  setBusy(true);
  try {
    const dark = document.documentElement.classList.contains("dark");
    const canvas = await html2canvas(el, { backgroundColor: dark ? "#111827" : "#ffffff", scale: 2, useCORS: true });
    await new Promise((resolve) => canvas.toBlob(async (blob) => {
      try {
        await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
        toast.success("Table image copied — paste into WhatsApp or email");
      } catch {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `report-${Date.now()}.png`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Image downloaded — attach it to your message");
      }
      resolve();
    }, "image/png"));
  } catch {
    toast.error("Could not capture the table");
  } finally {
    setBusy(false);
  }
}

/* ---------------- Ageing Matrix ---------------- */
function AgeingMatrix({ refreshKey }) {
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get("/reports/ageing").then(({ data }) => setData(data)); }, [refreshKey]);
  const totals = data ? data.buckets.reduce((a, b) => { a[b] = data.rows.reduce((s, r) => s + r.buckets[b], 0); return a; }, {}) : {};
  const exportCsv = () => downloadCsv("ageing-matrix",
    ["Customer", "Email", "Type", "Currency", "Excluded", ...data.buckets, "Total"],
    data.rows.map((r) => [r.customer_name, r.customer_email, r.source, r.currency, r.excluded ? "yes" : "no",
      ...data.buckets.map((b) => r.buckets[b].toFixed(2)), r.total.toFixed(2)]));

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={!data?.rows?.length} data-testid="export-report-csv-button">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {data.buckets.map((b) => (
            <Card key={b} className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: BUCKET_COLORS[b] }} />
                <span className="text-xs font-medium text-muted-foreground">{b} days</span>
              </div>
              <p className="font-heading text-lg font-bold">{num(totals[b])}</p>
            </Card>
          ))}
        </div>
      )}
      <Card className="overflow-hidden" data-testid="ageing-report-table">
        {!data ? <Loading /> : data.rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">
            <FileText className="w-8 h-8 mx-auto mb-3 text-muted-foreground" /> No data yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="text-xs uppercase tracking-wider">Customer</TableHead>
                  {data.buckets.map((b) => <TableHead key={b} className="text-xs uppercase tracking-wider text-right">{b}d</TableHead>)}
                  <TableHead className="text-xs uppercase tracking-wider text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r, i) => (
                  <TableRow key={i} className="hover:bg-muted/40">
                    <TableCell className="font-medium min-w-[160px] max-w-[260px]">
                      <span className={`break-words ${r.excluded ? "line-through text-muted-foreground" : ""}`}>{r.customer_name}</span>
                      {r.excluded && <Badge variant="outline" className="ml-2 text-[10px] bg-rose-500/10 text-rose-500 border-rose-500/20">excluded</Badge>}
                    </TableCell>
                    {data.buckets.map((b) => <TableCell key={b} className="text-right font-mono text-sm">{r.buckets[b] ? num(r.buckets[b]) : <span className="text-muted-foreground">—</span>}</TableCell>)}
                    <TableCell className="text-right font-mono font-semibold">{num(r.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}

/* ---------------- Incoming Month-wise ---------------- */
function IncomingMonthly({ refreshKey }) {
  const [data, setData] = useState(null);
  const [copying, setCopying] = useState(false);
  const cardRef = useRef(null);
  const now = useNow();
  useEffect(() => { setData(null); api.get("/reports/incoming-monthwise").then(({ data }) => setData(data)); }, [refreshKey]);
  const exportCsv = () => downloadCsv("payment-incoming-monthwise",
    ["Month", "Amount - INR", "Amount - Cr - INR"],
    data.rows.map((r) => [r.label, Math.round(r.amount_inr), r.amount_cr]));

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => copyCardImage(cardRef.current, setCopying)}
                disabled={!data || copying} data-testid="copy-monthwise-image-button">
          <Camera className="w-4 h-4 mr-2" /> {copying ? "Capturing…" : "Copy as image"}
        </Button>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={!data} data-testid="export-monthwise-csv-button">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>
      <Card ref={cardRef} className="overflow-hidden max-w-2xl" data-testid="incoming-monthwise-table">
        <div className="p-5 pb-3 border-b border-border/60">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-heading text-lg font-medium">Payment Incoming — Month Wise</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Expected collections by due month · all figures in INR</p>
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground whitespace-nowrap shrink-0" data-testid="monthwise-timestamp">
              <Clock className="w-3.5 h-3.5" /> As on {stamp(now)}
            </div>
          </div>
        </div>
        {!data ? <Loading /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="text-xs uppercase tracking-wider">Month</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Amount — INR</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Amount — Cr</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r, i) => (
                  <TableRow key={i} className={`hover:bg-muted/40 ${r.label === "Overdue" ? "bg-rose-500/5" : ""}`}>
                    <TableCell className="font-medium">
                      {r.label === "Overdue" ? <Badge variant="outline" className="bg-rose-500/10 text-rose-500 border-rose-500/20">Overdue</Badge> : r.label}
                    </TableCell>
                    <TableCell className="text-right font-mono">{inr(r.amount_inr)}</TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">{r.amount_cr ? r.amount_cr.toFixed(2) : "—"}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-t-2 border-border font-semibold bg-muted/30">
                  <TableCell>Totals</TableCell>
                  <TableCell className="text-right font-mono">{inr(data.total_inr)}</TableCell>
                  <TableCell className="text-right font-mono">{data.total_cr?.toFixed(2)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
        {data?.excluded_customers?.length > 0 && (
          <p className="text-[11px] text-muted-foreground px-5 pb-4 pt-3" data-testid="monthwise-excluded-note">
            <span className="font-medium text-foreground">Excluded (Won't Pay):</span>{" "}
            {data.excluded_customers.join(", ")}
          </p>
        )}
      </Card>
    </div>
  );
}

/* ---------------- Incoming Date-wise ---------------- */
function IncomingDatewise({ refreshKey }) {
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get("/reports/incoming-datewise").then(({ data }) => setData(data)); }, [refreshKey]);
  const exportCsv = () => downloadCsv("payments-incoming-datewise",
    ["From", "To", "Export - INR", "Domestic - INR", "Total - INR"],
    data.rows.map((r) => [r.from, r.to, Math.round(r.export_inr), Math.round(r.domestic_inr), Math.round(r.total_inr)]));

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={!data} data-testid="export-datewise-csv-button">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>
      <Card className="overflow-hidden" data-testid="incoming-datewise-table">
        <div className="p-5 pb-3 border-b border-border/60">
          <h3 className="font-heading text-lg font-medium">Payments Incoming Summary — Date Wise</h3>
          <p className="text-xs text-muted-foreground mt-0.5">10-day collection windows by due date · all figures in INR</p>
        </div>
        {!data ? <Loading /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="text-xs uppercase tracking-wider">From</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">To</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Export — INR</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Domestic — INR</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Total — INR</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r, i) => (
                  <TableRow key={i} className={`hover:bg-muted/40 ${r.from === "Overdue" ? "bg-rose-500/5" : ""}`}>
                    <TableCell className="font-medium">
                      {r.from === "Overdue" ? <Badge variant="outline" className="bg-rose-500/10 text-rose-500 border-rose-500/20">Overdue</Badge> : r.from}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{r.to === "Overdue" ? "" : r.to}</TableCell>
                    <TableCell className="text-right font-mono">{inr(r.export_inr)}</TableCell>
                    <TableCell className="text-right font-mono">{inr(r.domestic_inr)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{inr(r.total_inr)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-t-2 border-border font-semibold bg-muted/30">
                  <TableCell colSpan={2}>Totals</TableCell>
                  <TableCell className="text-right font-mono">{inr(data.totals.export_inr)}</TableCell>
                  <TableCell className="text-right font-mono">{inr(data.totals.domestic_inr)}</TableCell>
                  <TableCell className="text-right font-mono">{inr(data.totals.total_inr)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}

/* ---------------- Customer Monthly Ageing ---------------- */
function CustomerMonthly({ refreshKey }) {
  const [source, setSource] = useState("export");
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/reports/customer-monthly-ageing?source=${source}`).then(({ data }) => setData(data));
  }, [source, refreshKey]);
  const exportCsv = () => downloadCsv(`customer-monthly-ageing-${source}`,
    ["Customer Name", "Currency", ...data.columns, "Total"],
    data.rows.map((r) => [r.customer_name, r.currency, Math.round(r.overdue),
      ...data.month_labels.map((l) => Math.round(r.months[l] || 0)), Math.round(r.total)]));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <Tabs value={source} onValueChange={setSource}>
          <TabsList>
            <TabsTrigger value="export" data-testid="cust-monthly-tab-export">Export</TabsTrigger>
            <TabsTrigger value="domestic" data-testid="cust-monthly-tab-domestic">Domestic</TabsTrigger>
          </TabsList>
        </Tabs>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={!data?.rows?.length} data-testid="export-cust-monthly-csv-button">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>
      <Card className="overflow-hidden" data-testid="customer-monthly-table">
        <div className="p-5 pb-3 border-b border-border/60">
          <h3 className="font-heading text-lg font-medium capitalize">{source} Customer Monthly Ageing</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Outstanding split by expected collection month · original currency</p>
        </div>
        {!data ? <Loading /> : data.rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">No {source} receivables.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="text-xs uppercase tracking-wider">Customer Name</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Currency</TableHead>
                  {data.columns.map((c) => (
                    <TableHead key={c} className="text-xs uppercase tracking-wider text-right">{c}</TableHead>
                  ))}
                  <TableHead className="text-xs uppercase tracking-wider text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r, i) => (
                  <TableRow key={i} className="hover:bg-muted/40">
                    <TableCell className="font-medium min-w-[160px] max-w-[260px] break-words">{r.customer_name}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{r.currency || "—"}</Badge></TableCell>
                    <TableCell className="text-right font-mono text-sm">{r.overdue ? num(r.overdue) : <span className="text-muted-foreground">—</span>}</TableCell>
                    {data.month_labels.map((l) => (
                      <TableCell key={l} className="text-right font-mono text-sm">{r.months[l] ? num(r.months[l]) : <span className="text-muted-foreground">—</span>}</TableCell>
                    ))}
                    <TableCell className="text-right font-mono font-semibold">{num(r.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}

/* ---------------- FX Rates editor ---------------- */
function FxEditor({ onSaved }) {
  const [rates, setRates] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/settings/fx").then(({ data }) => setRates(data.rates));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      await api.put("/settings/fx", { rates });
      toast.success("Exchange rates updated");
      setOpen(false);
      onSaved?.();
    } catch {
      toast.error("Could not update rates (admin only)");
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="fx-rates-button">
          <Settings2 className="w-4 h-4 mr-2" /> FX Rates
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-primary" /> Exchange Rates to INR</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Export amounts are converted to INR using these rates for all INR reports.</p>
        <div className="space-y-3">
          {rates && Object.keys(rates).map((c) => (
            <div key={c} className="flex items-center gap-3">
              <Label className="w-16">1 {c} =</Label>
              <Input type="number" value={rates[c]} onChange={(e) => setRates((r) => ({ ...r, [c]: e.target.value }))}
                     data-testid={`fx-input-${c}`} className="max-w-[140px]" />
              <span className="text-sm text-muted-foreground">INR</span>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} data-testid="fx-save-button">Save rates</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const inrCell = (n) => (n ? Number(Math.round(n)).toLocaleString("en-IN") : "—");

/* ---------------- Due Date Verification ---------------- */
function TermsVerification({ refreshKey }) {
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get("/reports/terms-verification").then(({ data }) => setData(data)); }, [refreshKey]);
  const exportCsv = () => downloadCsv("due-date-verification",
    ["Invoice", "Customer", "Type", "Invoice Date", "Basis", "Terms (days)", "Expected Due", "Actual Due", "Variance (days)", "Status"],
    data.rows.map((r) => [r.invoice_no, r.customer_name, r.source, r.invoice_date ? fmtDate(r.invoice_date) : "", r.basis || "", r.terms_days,
      r.expected_due ? fmtDate(r.expected_due) : "", r.actual_due ? fmtDate(r.actual_due) : "", r.variance_days ?? "", r.status]));

  const badge = (s) => {
    if (s === "match") return <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"><CheckCircle2 className="w-3 h-3 mr-1" /> Match</Badge>;
    if (s === "mismatch") return <Badge variant="outline" className="bg-rose-500/10 text-rose-500 border-rose-500/20"><AlertTriangle className="w-3 h-3 mr-1" /> Mismatch</Badge>;
    return <Badge variant="outline" className="bg-muted text-muted-foreground">No date</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        {data && (
          <div className="flex gap-2 text-xs">
            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20">{data.counts.match} match</Badge>
            <Badge variant="outline" className="bg-rose-500/10 text-rose-500 border-rose-500/20">{data.counts.mismatch} mismatch</Badge>
            <Badge variant="outline" className="bg-muted text-muted-foreground">{data.counts.no_data} no date</Badge>
          </div>
        )}
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={!data?.rows?.length} data-testid="export-verify-csv-button">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>
      <Card className="overflow-hidden" data-testid="terms-verification-table">
        <div className="p-5 pb-3 border-b border-border/60">
          <h3 className="font-heading text-lg font-medium flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" /> Due Date Verification</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Expected due date is computed from each customer's payment-terms basis: <strong>Days From Invoice</strong> = terms days + invoice date (Col D), <strong>Days from BL</strong> = terms days + BL date (Col E), <strong>On Delivery</strong> = terms days + delivery date (Col F), <strong>Against BL</strong> = BL date (Col E). Variance +N = due later than expected, −N = earlier. BL/delivery dates apply to export invoices.</p>
        </div>
        {!data ? <Loading /> : data.rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">No open invoices to verify.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="text-xs uppercase tracking-wider">Invoice</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Customer</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Basis</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Invoice Date</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-center">Terms</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Expected Due</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Actual Due</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Variance</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r, i) => (
                  <TableRow key={i} className={`hover:bg-muted/40 ${r.status === "mismatch" ? "bg-rose-500/5" : ""}`}>
                    <TableCell className="font-mono text-xs">{r.invoice_no}</TableCell>
                    <TableCell className="font-medium min-w-[160px] max-w-[260px] break-words">{r.customer_name}
                      <div className="text-[11px] text-muted-foreground">{r.amount_display}</div>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px] font-normal bg-muted/50 whitespace-nowrap">{r.basis || "—"}</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.invoice_date ? fmtDate(r.invoice_date) : "—"}</TableCell>
                    <TableCell className="text-center text-sm">{r.terms_days ? `Net ${r.terms_days}d` : "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.expected_due ? fmtDate(r.expected_due) : "—"}</TableCell>
                    <TableCell className="text-sm">{r.actual_due ? fmtDate(r.actual_due) : "—"}</TableCell>
                    <TableCell className={`text-right font-mono text-sm ${r.variance_days ? "text-rose-500" : "text-muted-foreground"}`}>
                      {r.variance_days == null ? "—" : (r.variance_days > 0 ? `+${r.variance_days}d` : `${r.variance_days}d`)}
                    </TableCell>
                    <TableCell className="text-center">{badge(r.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}

const VIEWS = {
  ageing: { label: "Ageing Matrix", comp: AgeingMatrix },
  monthly: { label: "Incoming — Monthly", comp: IncomingMonthly },
  datewise: { label: "Incoming — Date Range", comp: IncomingDatewise },
  customer: { label: "Customer Monthly", comp: CustomerMonthly },
  verify: { label: "Due Date Verification", comp: TermsVerification },
};

export default function Reports() {
  const [view, setView] = useState("ageing");
  const [refreshKey, setRefreshKey] = useState(0);
  const View = VIEWS[view].comp;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Reports & Forecasts</h1>
          <p className="text-muted-foreground mt-1 text-sm">Ageing analysis and expected-collection forecasts by due date.</p>
        </div>
        <FxEditor onSaved={() => setRefreshKey((k) => k + 1)} />
      </div>
      <Tabs value={view} onValueChange={setView}>
        <TabsList className="flex-wrap h-auto">
          {Object.entries(VIEWS).map(([k, v]) => (
            <TabsTrigger key={k} value={k} data-testid={`report-tab-${k}`}>{v.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <View refreshKey={refreshKey} />
    </div>
  );
}
